/**
 * groovi-live-price.js — ONE shared live-price reflector (Feature A / D5).
 *
 * A shared <product-price> MutationObserver that, on any price change (a Kaching
 * Bundles tier, a native pack-size variant), recomputes its consumers together:
 *   • the installments line (.groovi-installments / [data-groovi-installments]) — I1
 *   • the per-unit badge ([data-sl-unit]) — U1 live / U2
 *   • the PDP free-shipping progress (groovi-freeship[data-context="product"])
 * Read-only: it never manages Kaching or theme state, only observes the visible price
 * and rewrites text. Idempotent (writes only when the text changes → no observer loop).
 * Currency/locale/settings all come from data-* on the elements — nothing hardcoded.
 *
 * Money parse: the visible price text is read as minor units assuming 2 decimals
 * (BRL ✓). The installments formula mirrors snippets/installments.liquid exactly:
 *   display_count = min(count, floor(price/min)); per = floor(price/display_count).
 *
 * U2 — VERIFIED live on build 1887 (2026-07-03). window.kachingBundlesApi exposes only
 * fetchDeals(); the selected pack count comes from the quantity input Kaching updates
 * (_updateQuantityInput) — on 3X the main input[name="quantity"] becomes "3" atomically
 * with the .price write. kachingSelectedPacks() reads the MAX of all quantity inputs.
 * The library adapter's `.kaching-bundles` widget selector DID turn out to match 1887.
 */
(function () {
  'use strict';

  // ── Dependency map (2026-07-04) ──────────────────────────────────────────────
  // This reflector is the shared runtime dependency of the server-rendered price features.
  // It only ever READS the live price and REWRITES existing elements — it never
  // creates them — so each feature degrades gracefully when its element is absent.
  //
  //   • UNIT_SEL  → the per-unit badge, `blocks/sl-unit.liquid` ("unidade - by
  //     studiolab"). As of 2026-07-04 this badge is also addable INSIDE product cards
  //     (whitelisted in `blocks/_product-card.liquid` + `_product-card-group.liquid`),
  //     so a collection/search page can hold MANY `[data-sl-unit]` nodes. The write is
  //     scoped to the main buy-box (inMainBuyBox) so card/cross-sell badges keep their
  //     correct server-rendered value. Units source: block setting → `custom.units`
  //     metafield (see Groovi_Metafields_Reference.md).
  //
  //   • INST_SEL  → the installments line, `snippets/installments.liquid`. As of
  //     2026-07-04 a product can opt OUT of installments entirely via the boolean
  //     metafield `custom.installments` (ON = hidden). When ON, the snippet renders
  //     NOTHING, so `[data-groovi-installments]` simply does not exist on the page and
  //     this reflector no-ops for it — no JS change was needed for the opt-out.
  // ─────────────────────────────────────────────────────────────────────────────
  var UNIT_SEL = '[data-sl-unit]';
  var INST_SEL = '[data-groovi-installments]';
  var SHIPPING_SEL = 'groovi-freeship[data-context="product"]';
  var PENDING_CLASS = 'sl-unit--pending';

  function parseMinor(text) {
    var digits = (text || '').replace(/\D/g, '');
    return digits ? parseInt(digits, 10) : NaN;
  }

  function formatMoney(major, locale, currency) {
    try {
      return new Intl.NumberFormat(locale || undefined, { style: 'currency', currency: currency }).format(major);
    } catch (e) {
      return major.toFixed(2);
    }
  }

  // First actually-visible `.price` within a scope (skips the hidden regular/sale
  // branch + a11y-hidden copies).
  function visiblePrice(scope) {
    var nodes = scope.querySelectorAll('.price');
    for (var i = 0; i < nodes.length; i++) {
      var p = nodes[i];
      if (p.offsetParent !== null && !p.closest('.price__hidden, .visually-hidden, [hidden]')) return p;
    }
    return null;
  }

  // The live main-product total, in minor units, read from <product-price> (the element
  // Kaching/variants rewrite) — never the whole document, which may hold a second
  // static .price (a cross-sell/buy-box) that would win as the first visible match.
  function liveTotalMinor() {
    var pp = document.querySelector('.product-information product-price');
    if (!pp) return NaN;
    var el = visiblePrice(pp);
    return el ? parseMinor(el.textContent) : NaN;
  }

  // Selected Kaching pack count. VERIFIED live on build 1887 (2026-07-03, live-DOM check
  // on the Maçã Verde x4 PDP): window.kachingBundlesApi exposes ONLY fetchDeals() — no
  // selected-quantity accessor — so the source is the quantity input Kaching updates via
  // _updateQuantityInput(tier.quantity). On tier select it sets the MAIN product-form
  // quantity input to the pack count (1X→"1", Dupla→"2", 3X→"3") atomically with the
  // .price write; the other quantity inputs (sticky ATC / quick-add) stay "1". Reading
  // the MAX across all input[name="quantity"] is robust to DOM order + returns the pack
  // count (≥1). Widget classes .kaching-bundles / .kaching-bundles__bar DO exist on 1887.
  function kachingSelectedPacks() {
    var packs = 1;
    var qs = document.querySelectorAll('input[name="quantity"]');
    for (var i = 0; i < qs.length; i++) {
      var n = parseInt(qs[i].value, 10);
      if (isFinite(n) && n > packs) packs = n;
    }
    return packs;
  }

  // Only the MAIN product's price consumers may be rewritten with the live main price.
  // The read is already scoped to <product-price>; the WRITE must be too, else the main
  // price gets stamped onto every OTHER product's server-rendered installments/unit line
  // (cross-sell, recommendations, related). I1/U2/D5 scope fix.
  //
  // ALLOWLIST (robust — the recommendation/related rails live OUTSIDE these, so any future
  // card type there is auto-excluded): the element must sit in the main PDP info column
  // (.product-information) or the sticky ATC bar (.sticky-add-to-cart).
  // MINUS nested cards: the "COMBINA COM" cross-sell (.groovi-crosssell-card) renders as a
  // block INSIDE .product-information, so it must be explicitly excluded; .resource-card /
  // .product-card guard any card variant that lands in the column too.
  function inMainBuyBox(el) {
    if (!el.closest('.product-information, .sticky-add-to-cart')) return false;
    if (el.closest('.groovi-crosssell-card, .resource-card, .product-card')) return false;
    return true;
  }

  function updateInstallments(totalMinor) {
    var els = document.querySelectorAll(INST_SEL);
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (!inMainBuyBox(el)) continue;
      var count = parseInt(el.dataset.instCount, 10) || 6;
      var min = parseInt(el.dataset.instMin, 10) || 0;
      var display = count;
      if (min > 0) {
        var byAmount = Math.floor(totalMinor / min);
        if (byAmount < count) display = byAmount;
      }
      if (display < 2) { el.hidden = true; continue; }
      var perMinor = Math.floor(totalMinor / display); // matches Liquid integer divided_by
      var per = formatMoney(perMinor / 100, el.dataset.locale, el.dataset.currency);
      var text = (el.dataset.instTpl || '').replace('__COUNT__', display).replace('__AMOUNT__', per);
      if (el.textContent !== text) el.textContent = text;
      el.hidden = false;
    }
  }

  // A Kaching bundle is ACTIVE on this product only when its widget has actually
  // rendered a tier bar — NOT when window.kachingBundlesApi merely exists. The app
  // embed defines that global on EVERY page (bundle or not), so keying off it made
  // bundle-LESS products (the kits — custom.units=30, no bars) route through the pack
  // multiplier too. Gating on the rendered `.kaching-bundles__bar` is the reliable
  // signal that a box multiplier applies here. (2026-07-06)
  function bundleActive() {
    return !!document.querySelector('.kaching-bundles__bar');
  }

  function updateUnits(totalMinor) {
    var bundle = bundleActive();
    var els = document.querySelectorAll(UNIT_SEL);
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (!inMainBuyBox(el)) continue;
      var units = parseInt(el.dataset.unitsPerPack, 10) || 0; // custom.units — units in ONE box/pack
      if (units <= 0) continue;
      // Total units on offer = selected boxes × units-per-box — matching the Kaching bar
      // subtitle exactly (1 / 2 / 3 Caixas → 4 / 8 / 12 pacotinhos when custom.units = 4).
      //   • Bundle present → boxes = the tier Kaching synced into the quantity input.
      //   • No bundle widget (kits, plain products) → boxes = 1, so per-unit is simply the
      //     single displayed price ÷ custom.units. A quantity stepper can't skew it (the PDP
      //     price is per-item, never a line total), and custom.units alone drives the divisor.
      var boxes = bundle ? kachingSelectedPacks() : 1;
      var perUnit = totalMinor / 100 / (boxes * units);
      var suffix = el.dataset.suffix || '';
      var text = formatMoney(perUnit, el.dataset.locale, el.dataset.currency) + (suffix ? ' ' + suffix : '');
      if (el.textContent !== text) el.textContent = text;
      el.classList.remove(PENDING_CLASS);
    }
  }

  function recompute() {
    var totalMinor = liveTotalMinor();
    if (!isFinite(totalMinor) || totalMinor < 0) return; // no live price → keep server values
    updateInstallments(totalMinor);
    updateUnits(totalMinor);
    // Kaching's visible price is already the discounted pack total. Plain PDPs
    // display a single variant price, so their quantity must be applied once.
    var quantity = document.querySelector('.product-information input[name="quantity"]');
    var count = bundleActive() ? 1 : Math.max(1, parseInt(quantity && quantity.value, 10) || 1);
    document.querySelectorAll(SHIPPING_SEL).forEach(function (bar) {
      if (!inMainBuyBox(bar)) return;
      var value = String(totalMinor * count);
      if (bar.dataset.productTotal !== value) bar.dataset.productTotal = value;
    });
  }

  function init() {
    // Bind even when all consumers are hidden, so adding the block in the editor works.
    if (!document.querySelector('.product-information')) return;

    var raf = null;
    var schedule = function () {
      if (raf) return; // coalesce bursts (idempotent writes already prevent loops)
      raf = requestAnimationFrame(function () { raf = null; recompute(); });
    };

    var observed = new WeakSet();
    function observePrices() {
      document.querySelectorAll('product-price').forEach(function (pp) {
        if (observed.has(pp)) return;
        observed.add(pp);
        new MutationObserver(schedule).observe(pp, { childList: true, characterData: true, subtree: true });
      });
    }
    observePrices();
    // Flavor navigation and Theme Editor reloads can replace the entire buy box.
    document.addEventListener('shopify:section:load', function () {
      observePrices();
      schedule();
    });

    // Belt-and-suspenders: Kaching may settle price + quantity a tick apart; re-run on
    // either. Delegated on document so it survives app re-renders.
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (t && (t.name === 'quantity' || (t.name && t.name.indexOf('kaching') === 0))) schedule();
    });
    document.addEventListener('input', function (e) {
      if (e.target && e.target.name === 'quantity') schedule();
    });

    recompute(); // correct any pre-selected tier / variant on load

    // Kaching renders asynchronously; its bar can paint AFTER the api global appears and
    // after it sets the default tier + quantity. Recompute until the bundle bar is actually
    // on the page (so the badge reflects the selected tier), or ~10s, then stop. Idempotent
    // writes make the extra passes free; a product with no bundle simply keeps the single-box
    // value computed from custom.units.
    var tries = 0;
    (function poll() {
      recompute();
      if (bundleActive()) return;
      if (tries++ < 40) setTimeout(poll, 250); // ~10s ceiling, then give up (no Kaching here)
    })();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
